import React, { useEffect, useMemo, useRef, useState } from "react";
import "./product-documents.component.scoped.scss";
import { Box, Button, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@material-ui/lab";
import { validateFiles } from "../../../../../../helpers/validate-file";
import { fileTypes } from "../../../../../../statics/file-types";
import { DocumentNameDialog } from "../../../../../../dialogs/document-name/document-name-dialog";
import { icons } from "../../../../../../statics/icons";
import {
  formatDate,
  showPopupToastMsg,
  showToastMsg,
} from "../../../../../../helpers";
import { ProductsService } from "../../../../../../services/products.services";
// import { formatDate } from "../../../../../../helpers";

interface ProductDocumentsComponentProps {
  productId: number;
  files: any[];
  loading: boolean;
  onDelete: (id: number) => void;
}

const ProductDocumentsComponent: React.FC<ProductDocumentsComponentProps> = ({
  productId,
  files,
  loading,
  onDelete,
}) => {
  const { t } = useTranslation();
  const productsService = useMemo(() => new ProductsService(), []);

  const inputFileRef = useRef(null);
  const [file, setFile] = useState<any>();
  const [documents, setDocuments] = useState<any[]>([]);
  const [showDocumentNameModal, setShowDocumentNameModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const addDocument = async (file: any) => {
    setSaving(true);
    const response = await productsService.addDocument(productId, file);
    setDocuments((prev: any) => [...prev, response.document]);
    if (response.message === "SUCCESS") {
      showToastMsg("success", t("DOCUMENT_ADDED_SUCCESSFULLY"));
    } else {
      showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
    }
    setSaving(false);
  };

  const handleClose = (value: any) => {
    if (value) {
      addDocument(value);
    }
    setShowDocumentNameModal(false);
  };

  const downloadFile = (url: string) => {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  useEffect(() => {
    if (files && files.length > 0) {
      setDocuments(files);
    }
  }, [files]);

  // ---------- Upload Document ----------
  const clickUpload = () => {
    if (!saving) {
      const element: any = inputFileRef.current;
      element.click();
    }
  };

  const onFileChange = async (e: any) => {
    await onValidateFile(e.target.files);
    const element: any = inputFileRef.current;
    element.value = "";
  };

  const onValidateFile = async (files: any) => {
    const filteredFiles = await validateFiles(files, fileTypes);
    setFile(filteredFiles[0]);
    setShowDocumentNameModal(true);
  };
  // ---------- End Upload Document ----------

  return (
    <>
      {documents.length > 0 && (
        <Box className="global__box__content p-0 mb-4">
          <Box className="global__box__content__header">
            <Typography
              variant="h4"
              color="textPrimary"
              className="text-capitalize"
            >
              {t("PRODUCTS_PAGE.RELEVANT_DOCUMENTS")}
            </Typography>
          </Box>

          <Box className="global__box__content__body p-0">
            <Box className="items">
              {documents.map((document: any, index: number) => {
                return (
                  <Box className="item" key={index}>
                    <Box className="file_info w-100">
                      {loading ? (
                        <Skeleton
                          variant="text"
                          width={"80%"}
                          height={22}
                          className="mb-1"
                        />
                      ) : (
                        <Typography
                          variant="h4"
                          color="textPrimary"
                          className="text-capitalize"
                          style={{ fontWeight: 500 }}
                        >
                          {document.name}
                        </Typography>
                      )}
                      {loading ? (
                        <Skeleton
                          variant="text"
                          width={"40%"}
                          height={18}
                          className="mb-1"
                        />
                      ) : (
                        <span className="date mb-1 d-block">
                          {t("UPLOADED_ON")}{" "}
                          {formatDate(document.createdAt, "DD.MM.YYYY")}
                        </span>
                      )}
                      {loading ? (
                        <Skeleton
                          variant="text"
                          width={"67%"}
                          height={20}
                          className="mb-1"
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          className="mb-1"
                          style={{ fontWeight: 400 }}
                        >
                          {t("UPLOADED_BY")} {document.user.email}
                        </Typography>
                      )}
                      {loading ? (
                        <Skeleton
                          variant="text"
                          width={"60%"}
                          height={20}
                          className="mb-1"
                        />
                      ) : (
                        <Typography variant="body2" style={{ fontWeight: 400 }}>
                          {t("FILE_NAME")}: {document.file}
                        </Typography>
                      )}
                    </Box>
                    <Box className="actions">
                      {loading ? (
                        <Skeleton
                          variant="rect"
                          width={80}
                          height={30}
                          style={{ borderRadius: "4px" }}
                        />
                      ) : (
                        <Button
                          variant="contained"
                          color="secondary"
                          style={{
                            color: "#fff",
                            background: "var(--secondary)",
                            fontSize: "0.8rem",
                            fontWeight: 300,
                            textTransform: "capitalize",
                          }}
                          className="mr-2"
                          size="small"
                          onClick={() => downloadFile(document.url)}
                        >
                          {t("DOWNLOAD")}
                        </Button>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}
      <Box className="global__box__content upload__documents p-0">
        <Box className="title">
          {loading ? (
            <Skeleton variant="text" width={200} height={20} />
          ) : (
            <Typography variant="h4" className="text-capitalize">
              {t("PRODUCTS_PAGE.UPLOAD_DOCUMENTS")}
            </Typography>
          )}
          {loading ? (
            <Skeleton
              variant="rect"
              width={100}
              height={30}
              style={{ borderRadius: "4px" }}
            />
          ) : (
            <Button
              variant="contained"
              color="primary"
              className="btn-primary-theme"
              onClick={clickUpload}
            >
              {t("UPLOAD")}
            </Button>
          )}
          <input
            type="file"
            ref={inputFileRef}
            onChange={onFileChange}
            style={{ display: "none" }}
            accept={fileTypes}
            disabled={saving}
          />
        </Box>
        <Box className="content">
          <Box
            className="text-center my-4"
            onClick={clickUpload}
            style={{ cursor: "pointer" }}
          >
            <Box className="drop-img">
              <img src={icons.DROP_FILE} alt="" />
            </Box>
            <Typography variant="h3">
              {t("CLICK_TO_UPLOAD_YOUR_FILE")}
            </Typography>
            <Typography variant="subtitle1">
              {t("ADD_FILE_AND_GIVE_NAME")}
            </Typography>
          </Box>
        </Box>
        {showDocumentNameModal && file && (
          <DocumentNameDialog
            file={file}
            path="products"
            segment={productId.toString()}
            show={showDocumentNameModal}
            onClose={handleClose}
          />
        )}
      </Box>
    </>
  );
};

export default ProductDocumentsComponent;
